// URL ↔ navigation bridge for Augment HR.
//
// The app drives navigation through state (navAdmin / navEmp + a `detail` object).
// This module maps that state to/from the browser URL so every tab gets its own
// address, deep links + refresh land on the right view, and back/forward work.
// It does NOT replace the existing navigation — App.jsx keeps its state and just
// syncs it here.

// Section views (single URL segment). Admin and employee share these view names;
// which set is shown is decided by role in App.jsx, so one map covers both.
const VIEW_TO_SLUG = {
  Dashboard: 'dashboard',
  Employees: 'employees',
  Attendance: 'attendance',
  Leave: 'leave',
  Recruitment: 'recruitment',
  'JD Library': 'jd-library',
  Performance: 'performance',
  Payroll: 'payroll',
  Policies: 'policies',
  'Organization Docs': 'organization-docs',
  Reports: 'reports',
  Settings: 'settings',
  Documents: 'documents',
  'Activity Log': 'activity-log',
  Entities: 'entities',
  Approvals: 'approvals',
  Configuration: 'configuration',
  Letters: 'letters',
  Requests: 'requests',
  'My Profile': 'my-profile',
  'Org Chart': 'org-chart',
  'Team Analytics': 'team-analytics',
  Training: 'training',
  'My Learning': 'my-learning',
  Competencies: 'competencies',
  'My Skills': 'my-skills',
  '360 Feedback': 'feedback-360',
  Feedback: 'feedback',
  Recognition: 'recognition',
  Awards: 'awards',
  Pulse: 'pulse',
  'Employee Relations': 'employee-relations',
  Insights: 'insights',
  Announcements: 'announcements',
  'Health & Safety': 'health-safety',
  Benefits: 'benefits',
  'My Benefits': 'my-benefits',
  Offboarding: 'offboarding',
  Disciplinary: 'disciplinary',
  'My Record': 'my-record',
  Succession: 'succession',
  Loans: 'loans',
  'My Loans': 'my-loans',
  'Performance Cycles': 'performance-cycles',
};
const SLUG_TO_VIEW = Object.fromEntries(
  Object.entries(VIEW_TO_SLUG).map(([view, slug]) => [slug, view]),
);

// Manager "Team" views live under /team/* so they don't collide with the
// employee's own /attendance, /leave, /performance.
const TEAM_VIEW_TO_SLUG = {
  "Team's Attendance": 'attendance',
  "Team's Leaves": 'leaves',
  "Team's Performance": 'performance',
};
const TEAM_SLUG_TO_VIEW = Object.fromEntries(
  Object.entries(TEAM_VIEW_TO_SLUG).map(([view, slug]) => [slug, view]),
);

// Detail drill-downs (one extra path segment carrying the record id).
const DETAIL_PREFIX = { employee: 'employees', candidate: 'candidates', job: 'jobs' };
// A detail view sits "on top of" a section — back returns here.
const DETAIL_SECTION = { employee: 'Employees', candidate: 'Recruitment', job: 'Recruitment' };

const DEFAULT_VIEW = 'Dashboard';

// Build the URL for the current navigation state.
export function buildPath(view, detail) {
  if (detail && detail.type && detail.id != null) {
    const prefix = DETAIL_PREFIX[detail.type];
    if (prefix) return `/${prefix}/${encodeURIComponent(detail.id)}`;
  }
  if (TEAM_VIEW_TO_SLUG[view]) return `/team/${TEAM_VIEW_TO_SLUG[view]}`;
  return `/${VIEW_TO_SLUG[view] || 'dashboard'}`;
}

// Parse a URL into { view, detail }. `view` is the section to highlight; `detail`
// is the drill-down (or null). Unknown paths fall back to the Dashboard.
export function parsePath(pathname) {
  const segs = String(pathname || '').split('/').filter(Boolean);
  if (segs.length === 0) return { view: DEFAULT_VIEW, detail: null };

  // /team/<slug>  → manager team view
  if (segs[0] === 'team' && segs[1]) {
    return { view: TEAM_SLUG_TO_VIEW[segs[1]] || DEFAULT_VIEW, detail: null };
  }

  // /<prefix>/<id>  → a detail drill-down
  if (segs[1]) {
    const type = Object.keys(DETAIL_PREFIX).find((t) => DETAIL_PREFIX[t] === segs[0]);
    if (type) {
      return {
        view: DETAIL_SECTION[type],
        detail: { type, id: decodeURIComponent(segs[1]) },
      };
    }
  }

  // /<section>
  return { view: SLUG_TO_VIEW[segs[0]] || DEFAULT_VIEW, detail: null };
}
